import { keepPreviousData, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';

// Use generated types from the API schema
type CreditNotesResponse = paths['/buyers/credit-notes']['get']['responses']['200']['content']['application/json'];
export type CreditNotesByMakerOrg = CreditNotesResponse['data'][0];
export type CreditNote = CreditNotesByMakerOrg['creditNotes'][number];

/**
 * Fetch credit notes from the /buyers/credit-notes endpoint
 */
export const fetchCreditNotes = async (): Promise<CreditNotesByMakerOrg[]> => {
  const response = await apiClient.get(`api/supplier-experience/buyers/credit-notes`).json<CreditNotesResponse>();

  return response?.data || [];
};

/**
 * Extracts market UUIDs that have credit notes enabled from credit notes data
 * @param creditNotesData - Array of credit notes data by maker organization
 * @returns Array of market UUIDs that have credit notes enabled
 */
export const extractMarketsWithCreditNotes = (creditNotesData: CreditNotesByMakerOrg[] | undefined): string[] => {
  return (creditNotesData ?? []).reduce<string[]>((markets, makerOrg) => {
    makerOrg.creditNotes?.forEach((creditNote: CreditNote) => {
      creditNote.userEnabledMarkets?.forEach((marketUuid: string) => {
        if (!markets.includes(marketUuid)) {
          markets.push(marketUuid);
        }
      });
    });
    return markets;
  }, []);
};

/**
 * Hook to fetch credit notes data
 *
 * @param options - Additional React Query options
 * @returns React Query result with credit notes data
 */
const useCreditNotes = (options?: Partial<UseQueryOptions<CreditNotesByMakerOrg[]>>) => {
  return useQuery({
    queryKey: ['credit-notes'],
    queryFn: () => fetchCreditNotes(),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

export default useCreditNotes;
